import React from 'react';
import Icon from '../../../components/AppIcon';

const SubscriptionOverview = ({ subscription, isPremium, onUpgrade, onCancelSubscription }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'var(--color-success)';
      case 'expiring': return 'var(--color-warning)';
      case 'expired': return 'var(--color-error)';
      default: return 'var(--color-muted-foreground)';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Aktiv';
      case 'expiring': return 'Utløper snart';
      case 'expired': return 'Utløpt';
      default: return 'Ukjent';
    }
  };

  return (
    <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Geometric accent */}
      <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '140px', height: '140px', border: '2px solid rgba(0,79,152,0.07)', borderRadius: '2px', transform: 'rotate(20deg)', pointerEvents: 'none' }} />

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-6 mb-6 lg:mb-8">
        <div className="flex-1">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-2 md:mb-3 text-foreground">
            {subscription?.planName}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground font-caption">
            {subscription?.description}
          </p>
        </div>
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded border-2"
          style={{
            borderColor: getStatusColor(subscription?.status),
            background: `${getStatusColor(subscription?.status)}15`
          }}
        >
          <Icon
            name={subscription?.status === 'active' ? 'CheckCircle2' : subscription?.status === 'expiring' ? 'AlertCircle' : 'XCircle'}
            size={18}
            color={getStatusColor(subscription?.status)}
          />
          <span className="text-sm md:text-base font-medium" style={{ color: getStatusColor(subscription?.status) }}>
            {getStatusText(subscription?.status)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <div
          className="p-4 md:p-6 rounded border-2 border-border bg-muted"
          style={{ transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease-out', cursor: 'default' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.18)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded flex items-center justify-center" style={{ background: 'rgba(0, 79, 152, 0.1)', transition: 'transform 300ms cubic-bezier(0.22,1,0.36,1)' }}>
              <Icon name="Calendar" size={20} color="var(--color-primary)" />
            </div>
            <span className="text-xs md:text-sm text-muted-foreground font-caption">Neste fornyelse</span>
          </div>
          <p className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">{subscription?.renewalDate}</p>
        </div>

        <div
          className="p-4 md:p-6 rounded border-2 border-border bg-muted"
          style={{ transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease-out', cursor: 'default' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.18)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded flex items-center justify-center" style={{ background: 'rgba(255, 87, 34, 0.1)', transition: 'transform 300ms cubic-bezier(0.22,1,0.36,1)' }}>
              <Icon name="CreditCard" size={20} color="var(--color-accent)" />
            </div>
            <span className="text-xs md:text-sm text-muted-foreground font-caption">Månedlig kostnad</span>
          </div>
          <p className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">{subscription?.monthlyPrice}</p>
        </div>

        <div
          className="p-4 md:p-6 rounded border-2 border-border bg-muted md:col-span-2 lg:col-span-1"
          style={{ transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease-out', cursor: 'default' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.18)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded flex items-center justify-center" style={{ background: 'rgba(16, 185, 129, 0.1)', transition: 'transform 300ms cubic-bezier(0.22,1,0.36,1)' }}>
              <Icon name="Shield" size={20} color="var(--color-success)" />
            </div>
            <span className="text-xs md:text-sm text-muted-foreground font-caption">Aktive tjenester</span>
          </div>
          <p className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">{subscription?.activeServices}</p>
        </div>
      </div>

      <div className="mt-6 lg:mt-8 pt-6 lg:pt-8 border-t-2 border-border">
        <h3 className="text-base md:text-lg font-semibold mb-4 text-foreground">Inkluderte fordeler</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {subscription?.benefits?.map((benefit, index) => (
            <div key={index} className="flex items-start gap-3">
              <Icon name="Check" size={20} color="var(--color-success)" className="flex-shrink-0 mt-1" />
              <span className="text-sm md:text-base text-foreground font-body">{benefit}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade CTA for free users */}
      {!isPremium && (
        <div
          style={{
            marginTop: '32px',
            background: 'linear-gradient(135deg, rgba(0,79,152,0.1) 0%, rgba(255,87,34,0.08) 100%)',
            border: '2px solid rgba(0,79,152,0.2)',
            borderRadius: 'var(--radius-sm)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <p style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '6px' }}>
                Klar for mer?
              </p>
              <p style={{ fontFamily: 'var(--font-caption)', fontSize: '14px', color: 'var(--color-muted-foreground)', maxWidth: '400px' }}>
                Oppgrader til Premium og få ubegrenset tilgang til alle funksjoner for kun 499 kr/mnd.
              </p>
            </div>
            <button
              onClick={onUpgrade}
              style={{
                background: 'var(--color-accent)',
                border: '2px solid var(--color-accent)',
                borderRadius: 'var(--radius-sm)',
                padding: '12px 28px',
                fontFamily: 'var(--font-heading)',
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#FFFFFF',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease-out',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(255,87,34,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.96)'}
              onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            >
              Oppgrader plan
            </button>
          </div>
        </div>
      )}
      {isPremium && onCancelSubscription && (
        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--color-border)' }}>
          <button
            onClick={onCancelSubscription}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '9px 18px',
              background: 'transparent',
              border: '1.5px solid rgba(255,87,34,0.35)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-heading)',
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'rgba(255,87,34,0.8)',
              cursor: 'pointer',
              transition: 'all 200ms ease-out',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,87,34,0.08)'; e.currentTarget.style.borderColor = '#FF5722'; e.currentTarget.style.color = '#FF5722'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,87,34,0.35)'; e.currentTarget.style.color = 'rgba(255,87,34,0.8)'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            Avslutt abonnement
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionOverview;